import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY_DIAL } from "../../constants/phone";
import { combineDialCodeAndNumber } from "../../utils/phone";
import { buildWhatsAppEmpresasContactUrl } from "../../data/cfoFraccionalData";

const DELAY_MS = 60000;
const STORAGE_SUBMITTED = "meu-empresas-contact-submitted";
const STORAGE_DISMISSED = "meu-empresas-contact-dismissed-session";

const EXCLUDED_PATHS = ["/empresas/iniciar-sesion"];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-[#0b1f3f] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1746a2]/30 focus:border-[#1746a2]";

export default function EmpresasContactPopup() {
  const { pathname } = useLocation();
  const isEmpresasRoute = pathname.startsWith("/empresas") && !EXCLUDED_PATHS.includes(pathname);

  const [visible, setVisible] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryDial, setCountryDial] = useState(DEFAULT_COUNTRY_DIAL);

  const countryOptions = useMemo(() => COUNTRY_OPTIONS, []);

  useEffect(() => {
    if (!isEmpresasRoute) {
      setVisible(false);
      return undefined;
    }

    if (localStorage.getItem(STORAGE_SUBMITTED) === "1") return undefined;
    if (sessionStorage.getItem(STORAGE_DISMISSED) === "1") return undefined;

    const timer = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [isEmpresasRoute, pathname]);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_DISMISSED, "1");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const normalizedPhone = combineDialCodeAndNumber(countryDial, phone);

    if (!trimmedName || !trimmedEmail || !normalizedPhone) {
      setError("Complete nombre, correo y teléfono.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Ingrese un correo electrónico válido.");
      return;
    }

    const whatsappUrl = buildWhatsAppEmpresasContactUrl({
      name: trimmedName,
      email: trimmedEmail,
      phone: normalizedPhone,
      pageUrl: window.location.pathname,
    });

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    localStorage.setItem(STORAGE_SUBMITTED, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-labelledby="empresas-contact-title"
        className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-6 sm:p-7"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">Empresas · MEU Aliado</p>
        <h3 id="empresas-contact-title" className="mt-2 text-xl font-extrabold text-[#0b1f3f] leading-snug pr-6">
          Déjenos sus datos y exploramos soluciones sin compromiso
        </h3>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed">
          Complete el formulario y le escribimos por WhatsApp para conocer las necesidades de su empresa.
        </p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <div>
            <label htmlFor="empresas-contact-name" className="block text-xs font-semibold text-[#0b1f3f] mb-1">
              Nombre *
            </label>
            <input
              id="empresas-contact-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="Su nombre"
              autoComplete="name"
              required
            />
          </div>

          <div>
            <label htmlFor="empresas-contact-email" className="block text-xs font-semibold text-[#0b1f3f] mb-1">
              Correo *
            </label>
            <input
              id="empresas-contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="correo@empresa.com"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label htmlFor="empresas-contact-phone" className="block text-xs font-semibold text-[#0b1f3f] mb-1">
              Teléfono / WhatsApp *
            </label>
            <div className="flex gap-2">
              <select
                value={countryDial}
                onChange={(e) => setCountryDial(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-2 py-2.5 text-xs text-[#0b1f3f] focus:outline-none focus:ring-2 focus:ring-[#1746a2]/30 max-w-[130px]"
                aria-label="Indicativo país"
              >
                {countryOptions.map((option) => (
                  <option key={option.code} value={option.dial}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                id="empresas-contact-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ""))}
                className={`${inputClass} flex-1`}
                placeholder="300 123 4567"
                autoComplete="tel-national"
                required
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 font-medium" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="flex w-full items-center justify-center rounded-full bg-[#25D366] px-6 py-3 text-white font-bold hover:bg-[#1da851] transition mt-1"
          >
            Enviar por WhatsApp
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="w-full text-sm text-slate-500 hover:text-slate-700 font-medium"
          >
            Ahora no, gracias
          </button>
        </form>
      </div>
    </div>
  );
}
